import { Theme as RadixTheme } from "@radix-ui/themes";
import { ThemeProvider } from "@material-ui/core/styles";
import React, { Suspense, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import appTheme from "./config/MuiAppTheme";
import "./styles/inch-paper.css";

import { Route, Switch as RouterSwitch } from "react-router-dom";

import axios from "axios";
import AppToolbar from "./templates/toolbar/AppToolbar";

// import 'normalize.css';
import {
  get_db_base,
  isIdBundle,
  reportFrontendErrors
} from "./helpers/Functions";
import {
  ACContext,
  get_ac_cookie,
  request_ac_config,
  set_ac_cookie,
  validate_hash
} from "./helpers/access_code";
import wfeMuiTheme from "./projects/_modular/config/muiTheme";
import HTMLRenderer from "./templates/HTMLRenderer";
import ErrorBoundary from "./templates/components/errorBoundaries/ErrorBoundary";
import ErrorFallback from "./templates/components/errorBoundaries/ErrorFallback";
import {
  BijEnergyGenerationContext,
  ExportStateContext,
  PageListContext,
  SelectedLocaleContext
} from "./templates/context";
import qcMuiTheme from "./templates/landing/config/landingMuiTheme";

import { updateQuery } from "./helpers/PosFunctions";
import LandingPreview from "./templates/landing";
import useProjectData from "./projects/bij-energy/data/project";

const POS = React.lazy(() => import("./projects/pos"));
const ModularProject = React.lazy(() => import("./projects/_modular"));
const BIJ = React.lazy(() => import("./projects/bij"));
const Scanners = React.lazy(() => import("./projects/scanners"));
const BijEnergy = React.lazy(() => import("./projects/bij-energy"));
const Resubscribe = React.lazy(() =>
  import("./templates/components/Resubscribe")
);

const ModularProjectLandingPreview = React.lazy(() =>
  import("./projects/_modular/landing")
);

// to ensure query string for language is only grabbed once
let queried_lang = false;

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function getAC(query) {
  for (const [key, value] of query) {
    if (key === value) return key;
  }
  return "";
}

function ProjectValidator_memo() {
  const params = useParams();
  const [loaded, setLoaded] = useState(false);
  const [acConfig, setACConfig] = useState(undefined);
  const [authAttempts, setAuthAttempts] = useState(0);
  const [authenticated, setAuthenticated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pdfExportData, setToolbarData] = useState(null);
  const [error, setError] = useState(false);
  const [selectedContact, setContact] = useState(null);
  const [selectedAudience, setAudience] = useState(null);
  const [selectedLocale, setLocale] = useState(null);
  const [exposeContent, setExposeContent] = useState(null);
  const [idOfSinglePageElementToExport, setIdOfSinglePageElementToExport] =
    useState(null);
  // const [open, setOpen] = React.useState(false);
  const [invalidAC, setInvalidAC] = useState(false);
  const [validatedAC, setValidatedAC] = useState(null);

  //for POS
  const [pageList, setPageList] = useState([]);
  const [frontendErrors, setFrontendErrors] = useState([]);
  // for BIJ
  const [pagesToExport, setPagesToExport] = useState([]);
  const urlParams = { history: useHistory(), location: useLocation() };
  //for POS, BIJ Energy
  const [exportState, setExportState] = useState({});
  // for BIJ Energy
  const blankEstimate = {
    type: "full",
    clientInfo: {},
    existingFleet: [{}],
    epsonFleet: [{}]
  };
  const filledEstimate = {
    type: "full",
    clientInfo: {
      clientName: "Tom Brady",
      country: 1,
      state: 5
    },
    existingFleet: [
      {
        brand: "Brother",
        id: 67,
        model: "MFC-L2759DW",
        quantity: 10,
        ppm: 10000,
        volume: 10 * 10000
      },
      {
        brand: "Canon",
        id: 176,
        model: "C5850i",
        quantity: 5,
        ppm: 15000,
        volume: 5 * 15000
      },
      {
        brand: "Konica Minolta",
        id: 990,
        model: "bizhubC364e",
        quantity: 10,
        ppm: 10000,
        volume: 10 * 10000
      }
    ],
    epsonFleet: [
      {
        brand: "Epson",
        id: 17,
        model: "WF-C21000",
        quantity: 10,
        ppm: 10000,
        volume: 10 * 10000
      },
      {
        brand: "Epson",
        id: 6,
        model: "AM-C5000",
        quantity: 5,
        ppm: 15000,
        volume: 5 * 15000
      },
      {
        brand: "Epson",
        id: 6,
        model: "AM-C5000",
        quantity: 10,
        ppm: 10000,
        volume: 10 * 10000
      }
    ]
  };
  const largeEstimate = {
    ...filledEstimate,
    existingFleet: [
      ...filledEstimate.existingFleet,
      ...filledEstimate.existingFleet,
      ...filledEstimate.existingFleet,
      ...filledEstimate.existingFleet
    ],
    epsonFleet: [
      ...filledEstimate.epsonFleet,
      ...filledEstimate.epsonFleet,
      ...filledEstimate.epsonFleet,
      ...filledEstimate.epsonFleet
    ]
  };
  const [estimate, setEstimate] = useState(
    !process.env.REACT_APP_BIJ_ENERGY_PREFILLED ? blankEstimate : filledEstimate
    // largeEstimate
  );
  //has to be done here so as not to reset the state when using links from router
  const [authContext, setAuthContext] = useState({
    authenticated: !!process.env.REACT_APP_BIJ_ENERGY_PREFILLED,
    app_access: false,
    manager_access: false,
    rememberMe: false
  });
  const [collapsedFooter, setCollapsedFooter] = useState(false);
  //has to be done here to avoid re-requesting it when using links from router
  const [{ projectData }, { loadPreview }] = useProjectData(params.project);
  const [generatedEstimateState, setGeneratedEstimateState] = useState({
    full: {
      estimate: null,
      reportUrl: null,
      filename: null,
      downloaded: false
    },
    simplified: {
      estimate: null,
      reportUrl: null,
      filename: null,
      downloaded: false
    }
  });

  const bijEnergyProps = {
    collapsedFooter,
    setCollapsedFooter,
    authContext,
    setAuthContext,
    projectData,
    loadPreview,
    estimate,
    setEstimate,
    filledEstimate,
    blankEstimate
  };

  useEffect(() => {
    //report errors to sns topic
    const reportErrors = async () => {
      await reportFrontendErrors(frontendErrors);
    };
    reportErrors();
  }, [frontendErrors]);

  // const [enteredAccessCode, setEnteredAccessCode] = useState('');
  // const [rememberCode, setRememberCode] = useState(true);

  // const handleClose = () => {
  //   setOpen(false);
  //   validateCode(enteredAccessCode)
  // };

  const toolbarActions = {
    setToolbarData,
    selectedContact,
    selectedAudience,
    setAudience,
    selectedLocale,
    setLocale,
    setIdOfSinglePageElementToExport,
    setExposeContent,
    exposeContent,
    pagesToExport,
    setPagesToExport
  };

  const toolbarData = {
    idOfSinglePageElementToExport,
    project: params.project
  };

  const toolbarsActionsAndData = {
    ...toolbarActions,
    ...toolbarData
  };

  // Collect route
  const access_code_enabled = acConfig?.preview_access === "access-code";

  useEffect(() => {
    if (params.project && params.project !== "bij-energy") {
      // Verify if project requires access code
      request_ac_config(params.project)
        .then((result) => setACConfig(result))
        .catch(() => setACConfig({}));
    } else {
      setACConfig({});
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const page = pagesToExport?.[0];
    //check if pagesToExport is in form [1] or {name: ProductName, id: 1, pages: [...pages]}
    const pageIsObject = !!page?.id;
    //if list of objects, get the list of those IDs to add to search param
    //otherwise send null to the function to remove that search param
    const paramValue = pageIsObject ? pagesToExport.map((p) => p.id) : null;
    updateQuery(urlParams, { comparisons: paramValue });
    // eslint-disable-next-line
  }, [pagesToExport]);

  const bundleIdPresent = isIdBundle(params.possibleBundleId);
  const skipAC = !!params.skipAC && bundleIdPresent;
  const query = useQuery();
  // const query_ac = unescape(query.get('ac') || '')
  const query_ac = getAC(query);

  useEffect(() => {
    let comparisons = query.get("comparisons");
    if (comparisons) {
      comparisons = comparisons.split(",");
      setPagesToExport(comparisons.map((c) => parseInt(c)));
    }

    // set initial value for selectedLocale from query string
    if (!queried_lang) {
      const langquery = query.get("lang");
      if (langquery) {
        if (!selectedLocale) {
          queried_lang = true;
          setLocale(langquery);
        } else if (typeof selectedLocale === "string") {
          if (langquery.toLowerCase() !== selectedLocale.toLowerCase()) {
            queried_lang = true;
            setLocale(langquery);
          }
        } else if (
          langquery.toLowerCase() !== selectedLocale.langquery.toLowerCase()
        ) {
          queried_lang = true;
          setLocale(langquery);
        }
      }
    }
    // eslint-disable-next-line
  }, []);

  // Handle access code authentication
  useEffect(() => {
    if (
      access_code_enabled === true &&
      !authenticated &&
      !invalidAC &&
      !skipAC
    ) {
      const existing_cookie = get_ac_cookie();
      const validate_cookie = existing_cookie.length > 0;
      const validate_code = query_ac.length > 0;
      const code_to_validate = validate_cookie ? existing_cookie : query_ac;
      const preserveAC = !!query.get("rememberAC") || false;
      const incrementAuthAttempts = () => setAuthAttempts(authAttempts + 1);
      const validateCode = (entered_access_code) => {
        if (!!entered_access_code && entered_access_code.length > 0) {
          // Validate against access code
          return validate_hash(
            params.project,
            entered_access_code,
            acConfig.access_hash
          )
            .then((ac_valid) => {
              if (ac_valid) {
                if (/* rememberCode && */ preserveAC)
                  set_ac_cookie(entered_access_code);

                setAuthenticated(true);
                return true;
              } else {
                return false;
              }
            })
            .finally(() => incrementAuthAttempts());
        }
      };
      if (code_to_validate.length > 0) {
        validateCode(code_to_validate).then((validity) => {
          if (validity) {
            setInvalidAC(false);
            setValidatedAC(code_to_validate);
          } else if (!validity && validate_cookie && validate_code) {
            validateCode(query_ac).then((validity) => {
              setInvalidAC(!validity);
              if (validity) setValidatedAC(query_ac);
            });
          } else setInvalidAC(true);
        });
      } else {
        setInvalidAC(true);
        // setOpen(true);
      }
    }
  }, [
    acConfig,
    authenticated,
    invalidAC,
    authAttempts,
    access_code_enabled,
    query_ac,
    skipAC,
    params.project,
    query
  ]);

  // Project not valid
  if (!params.project) {
    // report_pageview();
    return <React.Fragment />;
  }

  const contact_diana = {
    email: "Diana.Garcia@ea.epson.com",
    label: "Diana Garcia"
  };
  const contact_tara = {
    email: "Tara.Rautenbach@ea.epson.com",
    label: "Tara Rautenbach"
  };

  const resubscribeProjectsMap = {
    bij: {
      title: "Business Inkjet Comparison Tool",
      contact: contact_tara
    },
    "wf-pro-hc": {
      title: "WorkForce Pro HC Comparison Tool",
      contact: contact_diana
    }
  };

  if (!!resubscribeProjectsMap[params.project]) {
    return (
      <Resubscribe
        projectParam={params.project}
        projectInfo={resubscribeProjectsMap[params.project]}
      />
    );
  }

  if (!submitted) {
    // Validate project
    if (params.project !== "pos" && params.project !== "bij-energy") {
      axios
        .get(get_db_base(params.project))
        .then(() => setLoaded(true))
        .catch(() => setError(true));
    } else {
      setLoaded(true);
    }
    setSubmitted(true);
  }

  if (error) {
    // report_pageview();
    return <React.Fragment />;
  }

  if (acConfig === undefined) {
    return <React.Fragment />;
  }

  let show_spinner = !loaded || !pdfExportData;

  // if (pdfExportData)
  //   report_pageview();

  const globalSettings =
    pdfExportData?.additional?.dbDataRaw?.global_settings || {};

  const toolbarActionsAndData = {
    projectType: params.project,
    pdfExportData,

    setContact,
    selectedContact,
    setAudience,
    selectedAudience,
    setLocale,
    selectedLocale,
    setExposeContent,
    exposeContent,
    setPagesToExport,
    pagesToExport,

    globalSettings,

    setIdOfSinglePageElementToExport,
    idOfSinglePageElementToExport
  };

  const should_auth = access_code_enabled === true && !authenticated;
  const validated_url_ac =
    access_code_enabled === true && validatedAC
      ? `${validatedAC}=${validatedAC}`
      : "";

  // Prefix project paths with "/preview" for production
  const prefixPaths = (paths = []) =>
    paths.map((path) => `${process.env.REACT_APP_ROUTE_PATH_PREFIX}${path}`);

  const isPosExport = !!exportState.primaryProduct && params.project === "pos";
  const isBijEnergy = params.project === "bij-energy";
  const shouldHideToolbar =
    (params.project === "scanners" &&
      !!params.skipAC &&
      params.skipAC !== "qcl-spe") ||
    isBijEnergy;

  const mainClassNames = [
    idOfSinglePageElementToExport ? "spe" : "",
    isPosExport ? "enhancedExport" : "",
    shouldHideToolbar ? "hideToolbar" : ""
  ].join(" ");
  // const { project, possibleBundleId } = params;
  // Project loaded
  const mainContent = (
    <ErrorBoundary
      fallback={<ErrorFallback />}
      setFrontendErrors={setFrontendErrors}
      frontendErrors={frontendErrors}
    >
      {
        should_auth && invalidAC && <p>Access code required</p>
        /*
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
    >
      <DialogTitle id="form-dialog-title">Please Enter Access Code</DialogTitle>
      <DialogContent>
        <DialogContentText></DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Access Code"
          fullWidth
          onChange={(e) => setEnteredAccessCode(e.target.value)}
        />
       <FormControlLabel
        checked={rememberCode}
        control={<Switch color="primary" />}
        label="Remember code"
        onChange={(e) => setRememberCode(e.target.checked)}
      />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Enter
        </Button>
      </DialogActions>
    </Dialog>
    */
      }
      {(!should_auth || skipAC) && (
        <ACContext.Provider value={{ url_ac: validated_url_ac }}>
          <SelectedLocaleContext.Provider value={{ selectedLocale, setLocale }}>
            <PageListContext.Provider value={{ pageList, setPageList }}>
              <ExportStateContext.Provider
                value={{ exportState, setExportState }}
              >
                <BijEnergyGenerationContext.Provider
                  value={{ generatedEstimateState, setGeneratedEstimateState }}
                >
                  {!!show_spinner && (
                    <img id="spinner" src="/spinner.gif" alt="..." />
                  )}
                  <ThemeProvider theme={appTheme}>
                    {
                      <AppToolbar
                        {...toolbarActionsAndData}
                        exportState={exportState}
                        setExportState={setExportState}
                        generatedEstimateState={generatedEstimateState}
                        setGeneratedEstimateState={setGeneratedEstimateState}
                      />
                    }
                    <main className={mainClassNames}>
                      <Suspense fallback={<div />}>
                        <RouterSwitch>
                          {/* {prefixPaths(["/:project/product-details/:id"]).map(
                    (path, path_idx) => {
                      toolbarsActionsAndData.posProductDetailsId =
                        possibleBundleId;
                      return (
                        <Route path={path} key={path_idx}>
                          {project === "pos" ? (
                            <POS {...toolbarsActionsAndData} />
                          ) : (
                            <React.Fragment></React.Fragment>
                          )}
                        </Route>
                      );
                    }
                  )} */}
                          {/* <Route path={"/bij-energy/support"}>
                          <BijEnergy {...toolbarsActionsAndData} />
                        </Route> */}
                          {params.project !== "wf-pro-hc" && (
                            <Route
                              path={`/:project/landing/:bundleId?/:id0?/:id1?/:id2?/:id3?`}
                            >
                              <ThemeProvider theme={qcMuiTheme}>
                                <LandingPreview
                                  project={params.project}
                                  setToolbarData={setToolbarData}
                                  {...toolbarActionsAndData}
                                />
                              </ThemeProvider>
                            </Route>
                          )}
                          {prefixPaths([
                            "/:project/landing/:bundleId?/:unused?"
                          ]).map((path, path_idx) => (
                            <Route path={path} key={path_idx}>
                              <ThemeProvider theme={wfeMuiTheme}>
                                <ModularProjectLandingPreview
                                  {...toolbarsActionsAndData}
                                />
                              </ThemeProvider>
                            </Route>
                          ))}

                          <Route
                            path={`/:project/qcl-spe/:bundleId?/:id0?/:id1?/:id2?/:id3?`}
                          >
                            <Scanners
                              qclSpe={true}
                              {...toolbarsActionsAndData}
                            />
                          </Route>
                          {prefixPaths([
                            "/:project/:s3Key/:objectKey",
                            "/:project/:s3Key"
                          ]).map((path, path_idx) => {
                            const { project, s3Key, objectKey } = params;
                            return (
                              <Route path={path} key={path_idx}>
                                {project === "bij-energy" ? (
                                  <BijEnergy
                                    {...bijEnergyProps}
                                    {...toolbarsActionsAndData}
                                  />
                                ) : (
                                  <HTMLRenderer
                                    setToolbarData={setToolbarData}
                                  />
                                )}
                              </Route>
                            );
                          })}

                          {prefixPaths(["/:project"]).map((path, path_idx) => {
                            const { project } = params;
                            return (
                              <Route path={path} key={path_idx}>
                                {project === "pos" && (
                                  <POS {...toolbarsActionsAndData} />
                                )}
                                {project === "wf-pro-hc" && (
                                  <ModularProject {...toolbarsActionsAndData} />
                                )}
                                {project === "bij" && (
                                  <BIJ {...toolbarsActionsAndData} />
                                )}
                                {project === "scanners" && (
                                  <Scanners {...toolbarsActionsAndData} />
                                )}
                                {project === "bij-energy" && (
                                  <BijEnergy
                                    {...bijEnergyProps}
                                    {...toolbarsActionsAndData}
                                  />
                                )}
                              </Route>
                            );
                          })}
                        </RouterSwitch>
                      </Suspense>
                    </main>
                  </ThemeProvider>
                </BijEnergyGenerationContext.Provider>
              </ExportStateContext.Provider>
            </PageListContext.Provider>
          </SelectedLocaleContext.Provider>
        </ACContext.Provider>
      )}
    </ErrorBoundary>
  );
  return params.project === "bij-energy" ? (
    <RadixTheme style={{ backgroundColor: "#e0e0e0" }}>
      {mainContent}
    </RadixTheme>
  ) : (
    mainContent
  );
}
const ProjectValidator = React.memo(ProjectValidator_memo);
export default ProjectValidator;
